import { useEffect, useRef, useState, useCallback } from "react";
import * as React from "react";
import axios from "axios";
import { toast } from "sonner";
import AppShell from "@/components/layout/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import {
  ParentBillingChildrenCard,
  ParentBillingSetupDuplicateCard,
  ParentCenterSubscriptionCard,
  ParentCommunicationHero,
  ParentControlsPanel,
  ParentPhonePreview,
  ParentRevenueCard,
  ParentTrialSettingsCard,
  ParentInviteListCard,
  ParentPaymentSetupCard,
  ParentDirectorBilling,
  ParentZelleOverdueCard,
} from "@/components/parent-communication";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultSettings = {
  featureTitle: "Parent Communication",
  featureStatus: "phase_2_trial_live",
  heroImageUrl: "https://customer-assets.emergentagent.com/job_6ceb8fd6-1be7-4aed-8f48-c1411defaab2/artifacts/8jdfj3hu_image.png",
  supportingImageUrl: "https://customer-assets.emergentagent.com/job_6ceb8fd6-1be7-4aed-8f48-c1411defaab2/artifacts/ucblbjx0_image.png",
  draftParent: {
    parentName: "",
    email: "",
    phone: "",
    relationship: "Mother",
    childIds: [],
    trialDays: 7,
    notes: "",
  },
  inviteOptions: { emailInvite: true, textInvite: true, inviteMode: "live_link" },
  paymentOptions: {
    squareEnabled: true,
    zelleEnabled: false,
    paymentMode: "square_platform",
    monthlyAddOnLabel: "$24.99 / month add-on",
    annualAddOnLabel: "$269.89 / year add-on",
    monthlyCenterPrice: 24.99,
    annualCenterPrice: 269.89,
    centerSubscriptionActive: false,
    centerBillingCycle: null,
    centerSubscribedAt: null,
    centerSubscriptionEndDate: null,
    pendingCenterSubscription: null,
  },
  parentBillingSettings: {
    chargeParents: false,
    feePerChild: 0,
    monthlyFeePerChild: 0,
    yearlyFeePerChild: 0,
    billingFrequency: "monthly",
    billingDay: 1,
    billingMode: "auto",
    feePassthrough: true,
    activatedChildIds: [],
  },
  staffAccess: {
    canReplyToMessages: true,
    canShareUpdates: true,
    canSharePhotos: true,
    canShareDocuments: true,
  },
  parentPortalVisibility: {
    activityFeed: true,
    messages: true,
    documentUploads: true,
    invoicesAndPayments: true,
    centerAnnouncements: true,
    itemsNeeded: true,
    artworkAndPhotos: true,
  },
  trialSettings: {
    trialOffersEnabled: true,
    defaultTrialDays: 7,
    postTrialState: "upgrade_only",
  },
};

function PreviewCard({ user, API, availableChildren = [] }) {
  const [creds, setCreds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState("");
  const baseUrl = process.env.REACT_APP_BACKEND_URL.replace('/api', '');

  const copyText = (text) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/owner/test-parent-accounts`, {
        email: `preview.${user?.email}`,
        password: 'Preview@123',
        trialDays: 30,
        childId: selectedChildId || null,
      });
      setCreds({ email: res.data.email, password: 'Preview@123' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to create preview account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 space-y-3" data-testid="parent-portal-preview-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-emerald-800">Parent Portal Preview</p>
          <p className="text-xs text-emerald-700 mt-0.5">See exactly what parents experience — use the credentials below to log in</p>
        </div>
      </div>
      {availableChildren.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-xs font-semibold text-emerald-800 whitespace-nowrap">Preview as parent of:</label>
          <select
            value={selectedChildId}
            onChange={e => setSelectedChildId(e.target.value)}
            className="flex-1 min-w-[180px] text-sm border border-emerald-300 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
            data-testid="preview-child-selector"
          >
            <option value="">Select a child</option>
            {availableChildren.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      )}
      <button disabled={loading || !selectedChildId} onClick={generate}
        className="w-full flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition disabled:opacity-50"
        data-testid="generate-preview-btn">
        {loading ? 'Setting up...' : 'Generate Preview Access'}
      </button>
      {creds && (
        <div className="rounded-xl border border-emerald-300 bg-white p-4 space-y-3">
          <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Use these credentials on the parent login page — DO NOT use your owner email</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-slate-500 mb-1">Email</p>
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                <p className="text-sm font-mono font-semibold text-slate-800 flex-1 break-all">{creds.email}</p>
                <button onClick={() => copyText(creds.email)} className="text-xs text-emerald-600 font-semibold flex-shrink-0">Copy</button>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Password</p>
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                <p className="text-sm font-mono font-semibold text-slate-800 flex-1">{creds.password}</p>
                <button onClick={() => copyText(creds.password)} className="text-xs text-emerald-600 font-semibold">Copy</button>
              </div>
            </div>
          </div>
          <a href={`${baseUrl}/parent/login?preview=1`} target="_blank" rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-emerald-600 bg-white py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 transition"
            data-testid="open-parent-login-btn">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            Log In to Preview Parent Portal
          </a>
        </div>
      )}
    </div>
  );
}

export default function ParentCommunicationSettingsPage({ embedded = false }) {
  const { organization, user } = useAuth();
  const { subscription } = useSubscription();
  const isSignatureTier = subscription?.plan?.toLowerCase() === 'signature' ||
    ['todayskidz@comcast.net', 'todayskidz45@gmail.com'].includes(user?.email?.toLowerCase());
  const subscriptionCardRef = useRef(null);
  const [settings, setSettings] = useState(defaultSettings);
  const [lastActivityAt, setLastActivityAt] = useState(null);
  const [lastSignInTime, setLastSignInTime] = useState(null);
  const [lastSignOutTime, setLastSignOutTime] = useState(null);
  const [availableChildren, setAvailableChildren] = useState([]);
  const [billableChildren, setBillableChildren] = useState([]);
  const [recentInvites, setRecentInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processingChildId, setProcessingChildId] = useState(null);
  const [restoringChildId, setRestoringChildId] = useState(null);
  const [activatingChildId, setActivatingChildId] = useState(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("monthly");
  const [connectingSquare, setConnectingSquare] = useState(false);
  const [squareConnected, setSquareConnected] = useState(false);

  const applySettingsResponse = (data) => {
    setSettings({ ...defaultSettings, ...data.settings });
    if (data.lastActivityAt) setLastActivityAt(data.lastActivityAt);
    if (data.lastSignInTime) setLastSignInTime(data.lastSignInTime);
    if (data.lastSignOutTime) setLastSignOutTime(data.lastSignOutTime);
    setAvailableChildren(data.availableChildren || []);
    setRecentInvites(data.recentInvites || []);
    setBillableChildren(data.billableChildren || []);
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        if (window.location.search.includes("parent_comm_payment_success=1")) {
          const verifyResponse = await axios.post(`${API}/parent-communication/verify-payment`);
          window.history.replaceState({}, document.title, window.location.pathname);
          if (verifyResponse.data.activated) {
            toast.success("Center payment verified for Parent Communication");
          }
        }
        const response = await axios.get(`${API}/parent-communication/settings`);
        applySettingsResponse(response.data);
        // Check center's Square connection status
        try {
          const sq = await axios.get(`${API}/square/status`);
          setSquareConnected(sq.data.connected || false);
        } catch { /* ignore */ }
      } catch (error) {
        toast.error("Unable to load Parent Communication settings");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const centerActive = settings.paymentOptions.centerSubscriptionActive || isSignatureTier;
  const activeChildCount = settings.parentBillingSettings.activatedChildIds.length;

  const scrollToPayment = () => {
    subscriptionCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const updateSectionToggle = (section, field, value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/parent-communication/settings`, settings);
      applySettingsResponse(response.data);
      toast.success("Parent Communication settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save draft right now");
    } finally {
      setSaving(false);
    }
  };

  const updateTrialSettingsToggle = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      trialSettings: {
        ...prev.trialSettings,
        [field]: value,
      },
    }));
  };

  const updateTrialSettingsNumber = (field, value) => {
    const normalizedValue = Math.max(1, Number(value || 1));
    setSettings((prev) => ({
      ...prev,
      trialSettings: {
        ...prev.trialSettings,
        [field]: normalizedValue,
      },
      draftParent: {
        ...prev.draftParent,
        trialDays: normalizedValue,
      },
    }));
  };

  const updateBillingSetting = (field, value) => {
    const numericFields = ["feePerChild", "monthlyFeePerChild", "yearlyFeePerChild"];
    const normalizedValue = numericFields.includes(field) ? Number(value || 0) : value;

    setSettings((prev) => ({
      ...prev,
      parentBillingSettings: (() => {
        const nextBilling = {
          ...prev.parentBillingSettings,
          [field]: normalizedValue,
        };

        if (field === "monthlyFeePerChild" && nextBilling.billingFrequency === "monthly") {
          nextBilling.feePerChild = normalizedValue;
        }

        if (field === "yearlyFeePerChild" && nextBilling.billingFrequency === "yearly") {
          nextBilling.feePerChild = normalizedValue;
        }

        if (field === "billingFrequency") {
          nextBilling.feePerChild = normalizedValue === "yearly"
            ? Number(nextBilling.yearlyFeePerChild || 0)
            : Number(nextBilling.monthlyFeePerChild || 0);
        }

        if (field === "feePerChild") {
          if (nextBilling.billingFrequency === "yearly") {
            nextBilling.yearlyFeePerChild = normalizedValue;
          } else {
            nextBilling.monthlyFeePerChild = normalizedValue;
          }
        }

        return nextBilling;
      })(),
    }));
  };

  const toggleBillingChild = async (childId, checked) => {
    setProcessingChildId(childId);
    try {
      const response = await axios.post(`${API}/parent-communication/activate-child`, {
        childId,
        activate: checked,
      });
      applySettingsResponse(response.data);
      if (checked) {
        toast.success("Child activated and parent invite link created");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update child activation");
    } finally {
      setProcessingChildId(null);
    }
  };

  const restoreBillingChild = async (childId) => {
    setRestoringChildId(childId);
    try {
      const response = await axios.post(`${API}/parent-communication/restore-child`, { childId });
      applySettingsResponse(response.data);
      toast.success("Invite restored to create invite first state");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to restore this child row");
    } finally {
      setRestoringChildId(null);
    }
  };

  const [startingCheckout, setStartingCheckout] = useState(false);
  const [sendingInviteChildId, setSendingInviteChildId] = useState(null);
  const [revokingChildId, setRevokingChildId] = useState(null);

  const updatePaymentOption = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      paymentOptions: { ...prev.paymentOptions, [field]: value },
    }));
  };

  const connectSquare = async () => {
    setConnectingSquare(true);
    try {
      const res = await axios.get(`${API}/square/authorize`);
      window.location.href = res.data.authorization_url;
    } catch {
      toast.error("Unable to start Square connection");
      setConnectingSquare(false);
    }
  };

  const manualActivateChild = async (childId) => {
    setActivatingChildId(childId);
    try {
      await axios.post(`${API}/parent-communication/manual-activate-child`, { childId });
      const response = await axios.get(`${API}/parent-communication/settings`);
      applySettingsResponse(response.data);
      toast.success("Parent manually activated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to activate");
    } finally {
      setActivatingChildId(null);
    }
  };

  const cancelSubscription = async () => {
    try {
      const response = await axios.post(`${API}/parent-communication/cancel-subscription`);
      applySettingsResponse(response.data);
      toast.success("Subscription cancelled");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to cancel subscription");
    } finally {
      setCancellingSubscription(false);
    }
  };

  const sendInvite = async (childId, sendEmail, sendSms) => {
    setSendingInviteChildId(childId);
    try {
      const response = await axios.post(`${API}/parent-communication/send-invite`, {
        childId, sendEmail, sendSms,
      });
      applySettingsResponse(response.data);
      const via = [sendEmail && "email", sendSms && "text"].filter(Boolean).join(" + ");
      toast.success(`Invite sent via ${via}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to send invite");
    } finally {
      setSendingInviteChildId(null);
    }
  };

  const revokeAccess = async (childId) => {
    if (!window.confirm("Pause this parent's portal access? They will see a payment-required screen until you reinstate them. Their trial dates and password are preserved.")) return;
    setRevokingChildId(childId);
    try {
      await axios.post(`${API}/parent-communication/revoke-access`, { childId });
      const response = await axios.get(`${API}/parent-communication/settings`);
      applySettingsResponse(response.data);
      toast.success("Parent access paused");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to pause access");
    } finally {
      setRevokingChildId(null);
    }
  };

  const reinstateAccess = async (childId) => {
    setRevokingChildId(childId);
    try {
      await axios.post(`${API}/parent-communication/reinstate-access`, { childId });
      const response = await axios.get(`${API}/parent-communication/settings`);
      applySettingsResponse(response.data);
      toast.success("Parent access reinstated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to reinstate access");
    } finally {
      setRevokingChildId(null);
    }
  };

  const resetInvite = async (childId) => {
    if (!window.confirm("Reset this invite to a fresh state? The old invite link will stop working, the parent's password will be cleared, and a brand-new invite link will be generated. Click Send Invite afterwards to deliver the new link.")) return;
    setRestoringChildId(childId);
    try {
      await axios.post(`${API}/parent-communication/reset-invite`, { childId });
      const response = await axios.get(`${API}/parent-communication/settings`);
      applySettingsResponse(response.data);
      toast.success("Invite reset — send the new link");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to reset invite");
    } finally {
      setRestoringChildId(null);
    }
  };

  if (loading) {
    const spinner = (
      <div className="flex items-center justify-center py-24" data-testid="parent-communication-loading-state">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
      </div>
    );
    if (embedded) return spinner;
    return <AppShell pageTitle="Family SMS Access Portal" breadcrumb>{spinner}</AppShell>;
  }

  const wrap = (children) => embedded
    ? children
    : <AppShell pageTitle="Family SMS Access Portal" breadcrumb>{children}</AppShell>;

  return wrap(
      <div className="space-y-8" data-testid="parent-communication-settings-page">
        <ParentCommunicationHero heroImageUrl={settings.heroImageUrl} organizationName={organization?.name} />

        {/* ── Permanent Director Instruction Card — always visible below hero ── */}
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5 space-y-4" data-testid="director-instruction-card">
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-emerald-700 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
            <p className="font-bold text-emerald-800 text-base">Director Guide — How This Works & How to Sell It</p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-white p-4 space-y-1">
            {isSignatureTier ? (
              <>
                <p className="text-sm font-bold text-emerald-800 flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  Step 1 — Subscription Active
                </p>
                <p className="text-sm text-slate-600">
                  Signature Tier — Lifetime access included. Proceed to Step 2 to set your parent fee.
                </p>
              </>
            ) : centerActive ? (
              <>
                <p className="text-sm font-bold text-emerald-800 flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  Step 1 — Subscription Active ({settings.paymentOptions.centerBillingCycle === "annual" ? "Annual" : "Monthly"})
                </p>
                <p className="text-sm text-slate-600">
                  Your center's {settings.paymentOptions.centerBillingCycle === "annual" ? "annual" : "monthly"} subscription is active and in good standing. Proceed to Step 2 below to set your parent fee.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-bold text-emerald-800">Step 1 — Subscribe to unlock all features</p>
                <p className="text-sm text-slate-600">
                  Complete the center payment below ($24.99/month or $269.89/year). Once active, all parent activation and billing features unlock immediately.
                </p>
                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900 leading-relaxed">
                  <p className="font-bold text-emerald-800 mb-1.5">Messaging Fair Use Policy</p>
                  <p className="mb-1.5">
                    Your $24.99/month plan includes generous messaging designed to easily support everyday childcare communication, such as parent updates, reminders, alerts, menus, children's activities and important notifications, etc. If your center happens to reach higher messaging levels, no worries—just reach out to us, and we'll help you continue sending messages for the rest of the month with a simple bulk pricing option.
                  </p>
                  <p>
                    This plan is built for regular day-to-day communication and isn't intended for large-scale automated marketing. If you need higher-volume messaging, we're happy to work with you—just contact us at <a href="mailto:admin@simplesignin.com" className="underline font-semibold">admin@simplesignin.com</a> for options.
                  </p>
                </div>
                <button onClick={scrollToPayment} className="inline-flex items-center gap-2 mt-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition">
                  Subscribe Now →
                </button>
              </>
            )}
          </div>
          <div className="rounded-xl border border-emerald-200 bg-white p-4 space-y-1">
            <p className="text-sm font-bold text-emerald-800">Step 2 — Set your parent fee and save</p>
            <p className="text-sm text-slate-600">
              Scroll down to <strong>Parent Billing Setup</strong> and enter your monthly and/or yearly fee per child, then click <strong>Save Billing Settings</strong>. Set to $0 for free parent access. <strong>This must be saved before you can activate children.</strong>
            </p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-white p-4 space-y-1">
            <p className="text-sm font-bold text-emerald-800">Step 3 — Activate parents from your child list</p>
            <p className="text-sm text-slate-600">
              Check each child you want to enroll. Choose Email and/or Text, then hit <strong>Send Invite</strong>. Parents receive a link, create their account, and get a free trial automatically. After signing in the first time, parents bookmark <strong>https://login.todayskidzsimplesignin.com/parent/login</strong> for all future visits.
            </p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-white p-4 space-y-1">
            <p className="text-sm font-bold text-emerald-800">Step 4 — Use Family Communications Hub to reach all parents at once</p>
            <p className="text-sm text-slate-600">
              Once parents are in the portal, go to <strong>Family Communications Hub</strong> in the sidebar to send daily menus, class project updates, announcements, and items needed — all by email and/or text.
            </p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2">
            <p className="text-sm font-bold text-amber-800">Revenue Opportunity — This feature pays for itself</p>
            <p className="text-sm text-slate-600">Set a monthly fee per child. You decide the amount — the center keeps it.</p>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {[{fee:'$3',n:30,mo:'$90'},{fee:'$5',n:30,mo:'$150'},{fee:'$10',n:30,mo:'$300'}].map(e => (
                <div key={e.fee} className="rounded-lg bg-white border border-amber-200 p-2 text-center">
                  <p className="text-base font-black text-amber-700">{e.mo}<span className="text-xs font-normal text-slate-500">/mo</span></p>
                  <p className="text-[10px] text-slate-500">{e.fee}/child × {e.n} families</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
            <p className="text-sm font-bold text-blue-800">What to say to parents — your sales script</p>
            <div className="rounded-lg bg-white border border-blue-100 p-3 text-sm text-slate-700 italic leading-relaxed">
              "We're rolling out a Family Portal where you'll receive daily menus, class updates, announcements, documents, and direct messages from our staff — all in one place on your phone. You'll get a free {'{trial}'}-day trial so you can experience it before anything is due. After your trial, it's just ${'{fee}'} a month to stay connected to everything happening with your child."
            </div>
            <p className="text-[11px] text-blue-600">Replace {'{trial}'} with your trial days and {'{fee}'} with your monthly fee — both are set below.</p>
          </div>
        </div>

        {/* Owner-only Parent Portal Preview */}
        <PreviewCard user={user} API={API} availableChildren={availableChildren} />

        <div ref={subscriptionCardRef}>
          <ParentCenterSubscriptionCard settings={settings} selectedPlan={selectedPlan} onPlanChange={setSelectedPlan} ownerEmail={user?.email} onCancelSubscription={cancelSubscription} cancelling={cancellingSubscription} />
        </div>

        {isSignatureTier && (
          <div className="flex items-center justify-center gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 py-3 text-emerald-700" data-testid="parent-comm-lifetime-badge">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            <p className="text-sm font-bold tracking-wide">Signature Tier — Lifetime Access</p>
          </div>
        )}

        <div className={`relative ${!centerActive && !isSignatureTier ? 'rounded-2xl overflow-hidden' : ''}`}>
          {/* Locked blur overlay */}
          {!centerActive && !isSignatureTier && (
            <div className="absolute inset-0 z-10 bg-white/75 backdrop-blur-[3px] rounded-2xl flex flex-col items-center justify-center gap-3 text-center p-8" data-testid="parent-comm-locked-banner">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </div>
              <p className="font-bold text-slate-800 text-lg">Subscribe to unlock all features</p>
              <p className="text-sm text-slate-500 max-w-sm">Complete payment above to activate parent enrollment, billing, invites, and the full portal management suite</p>
              <button onClick={scrollToPayment} className="rounded-full bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 transition">
                Subscribe Now →
              </button>
            </div>
          )}
        <div className="grid gap-8 2xl:grid-cols-[1.05fr_0.95fr]">
          <div className={`space-y-8 transition-opacity duration-300 ${!centerActive ? "pointer-events-none opacity-30 select-none" : ""}`}>
            <ParentBillingSetupDuplicateCard settings={settings} canActivate={centerActive} activeChildCount={activeChildCount} totalChildren={availableChildren.length} onToggle={(field, value) => updateBillingSetting(field, value)} onFieldChange={updateBillingSetting} onSave={saveSettings} saving={saving} onGoToPayment={scrollToPayment} />
            <ParentBillingChildrenCard rows={billableChildren.map((row) => ({ ...row, billingActive: settings.parentBillingSettings.activatedChildIds.includes(row.id), fee: settings.parentBillingSettings.feePerChild || row.fee }))} canActivate={centerActive} onToggleChild={toggleBillingChild} onRestoreChild={restoreBillingChild} onManualActivate={manualActivateChild} onSendInvite={sendInvite} onRevokeAccess={revokeAccess} onReinstateAccess={reinstateAccess} onResetInvite={resetInvite} processingChildId={processingChildId} restoringChildId={restoringChildId} activatingChildId={activatingChildId} sendingInviteChildId={sendingInviteChildId} revokingChildId={revokingChildId} />
            <ParentTrialSettingsCard trialSettings={settings.trialSettings} onToggle={updateTrialSettingsToggle} onNumberChange={updateTrialSettingsNumber} disabled={!centerActive} />
            <ParentPaymentSetupCard settings={settings} onFieldChange={updatePaymentOption} onSave={saveSettings} saving={saving} squareConnected={squareConnected} onConnectSquare={connectSquare} connectingSquare={connectingSquare} disabled={!centerActive} />
            <ParentControlsPanel settings={settings} onSectionToggle={updateSectionToggle} disabled={!centerActive} />
          </div>

          <div className="space-y-8">
            <ParentPhonePreview settings={settings} heroImageUrl={settings.heroImageUrl} supportingImageUrl={settings.supportingImageUrl} organizationName={organization?.name} lastActivityAt={lastActivityAt} firstChildName={availableChildren?.[0]?.name || billableChildren?.[0]?.childName} firstChildPhoto={availableChildren?.[0]?.photoUrl} lastSignInTime={lastSignInTime} lastSignOutTime={lastSignOutTime} />
            <ParentDirectorBilling centerActive={centerActive} />
            <ParentZelleOverdueCard centerActive={centerActive} />
          </div>
        </div>
        </div>
      </div>
  );
}
